import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import PercentIcon from "@mui/icons-material/Percent";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../context/SnackbarContext";
import { cupoService } from "../../services/cupo/cupoService";
import { productService } from "../../services/product/productService";
import { Product, ProductType } from "../../types/api.types";

type CupoProductoCreateFormData = {
  tipoProductoId: string;
  productoId: string | undefined;
  porcentaje: string;
};

const TIPO_PAGE = 0;
const TIPO_ROW_PER_PAGE = 20;
const PRODUCT_PAGE = 0;
const PRODUCT_ROW_PER_PAGE = 20;

const CuposProductoCreatePage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [tiposProducto, setTiposProducto] = useState<ProductType[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [tipoProductoOpen, setTipoProductoOpen] = useState(false);
  const [tipoProductoInputValue, setTipoProductoInputValue] = useState("");
  const [selectedTipoProducto, setSelectedTipoProducto] =
    useState<ProductType | null>(null);

  const [productos, setProductos] = useState<Product[]>([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [productoOpen, setProductoOpen] = useState(false);
  const [productoInputValue, setProductoInputValue] = useState("");
  const [selectedProducto, setSelectedProducto] = useState<Product | null>(
    null,
  );

  useEffect(() => {
    let active = true;

    if (!tipoProductoOpen) {
      return undefined;
    }

    const fetchTipos = async () => {
      setLoadingTipos(true);
      try {
        const list = await productService.getAllProductType(
          TIPO_PAGE,
          TIPO_ROW_PER_PAGE,
          tipoProductoInputValue,
        );
        if (active) {
          setTiposProducto(Array.isArray(list) ? list : []);
        }
      } catch {
        if (active) {
          showSnackbar("Error al buscar tipos de producto", "error", 4000);
          setTiposProducto([]);
        }
      } finally {
        if (active) {
          setLoadingTipos(false);
        }
      }
    };

    const delayDebounceFn = setTimeout(() => {
      void fetchTipos();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [tipoProductoInputValue, tipoProductoOpen, showSnackbar]);

  const tipoOptions = useMemo(() => {
    const byId = new Map(
      tiposProducto.map((t) => [String(t.productTypeID), t]),
    );
    if (
      selectedTipoProducto &&
      !byId.has(String(selectedTipoProducto.productTypeID))
    ) {
      return [selectedTipoProducto, ...tiposProducto];
    }
    return tiposProducto;
  }, [tiposProducto, selectedTipoProducto]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CupoProductoCreateFormData>({
    defaultValues: {
      tipoProductoId: "",
      productoId: undefined,
      porcentaje: "",
    },
  });

  const tipoProductoId = watch("tipoProductoId");

  const productoOptions = useMemo(() => {
    const byId = new Map(productos.map((p) => [p.productID, p]));
    if (selectedProducto && !byId.has(selectedProducto.productID)) {
      return [selectedProducto, ...productos];
    }
    return productos;
  }, [productos, selectedProducto]);

  useEffect(() => {
    if (!tipoProductoId) {
      setSelectedTipoProducto(null);
      setTipoProductoInputValue("");
    }
    setValue("productoId", "");
    setSelectedProducto(null);
    setProductoInputValue("");
    setProductos([]);
  }, [tipoProductoId, setValue]);

  useEffect(() => {
    let active = true;

    if (!productoOpen || !tipoProductoId) {
      return undefined;
    }

    const fetchProductos = async () => {
      setLoadingProductos(true);
      try {
        const res = await productService.getProductsByProductType(
          PRODUCT_PAGE,
          PRODUCT_ROW_PER_PAGE,
          tipoProductoId,
          productoInputValue,
        );
        if (active) {
          setProductos(res?.products ?? []);
        }
      } catch {
        if (active) {
          showSnackbar("Error al buscar productos", "error", 4000);
          setProductos([]);
        }
      } finally {
        if (active) {
          setLoadingProductos(false);
        }
      }
    };

    const delayDebounceFn = setTimeout(() => {
      void fetchProductos();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [productoInputValue, productoOpen, tipoProductoId, showSnackbar]);

  const onSubmit = async (data: CupoProductoCreateFormData) => {
    const percentage = Number(String(data.porcentaje).replace(",", "."));
    try {
      await cupoService.insertClassificationByProduct({
        productTypeId: data.tipoProductoId,
        productId: data.productoId?.trim() || undefined,
        percentage,
      });
      showSnackbar("Cupo registrado correctamente", "success", 3000);
      navigate("/cupos/producto");
    } catch (e: any) {
      showSnackbar(
        e?.response?.data?.message ?? "Error al guardar el cupo",
        "error",
        4000,
      );
    }
  };

  const handleCancel = () => {
    navigate("/cupos/producto");
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/home");
              }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/cupos/producto");
              }}
            >
              Cupos por producto
            </Link>
            <Typography color="text.primary">Crear producto</Typography>
          </Breadcrumbs>

          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <Typography
              variant="h5"
              component="h1"
              fontWeight={600}
              gutterBottom
            >
              Crear producto
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Seleccioná el tipo de producto, opcionalmente un producto del
              listado filtrado, y el porcentaje de cupo (0 a 100).
            </Typography>
          </motion.div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 3,
                rowGap: 3,
              }}
            >
              <Controller
                name="tipoProductoId"
                control={control}
                rules={{ required: "Debés seleccionar un tipo de producto" }}
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    open={tipoProductoOpen}
                    onOpen={() => setTipoProductoOpen(true)}
                    onClose={() => setTipoProductoOpen(false)}
                    options={tipoOptions}
                    getOptionLabel={(option) => option.name}
                    loading={loadingTipos}
                    inputValue={tipoProductoInputValue}
                    value={
                      tipoOptions.find(
                        (t) => String(t.productTypeID) === field.value,
                      ) ||
                      selectedTipoProducto ||
                      null
                    }
                    onChange={(_, newValue) => {
                      field.onChange(
                        newValue ? String(newValue.productTypeID) : "",
                      );
                      setSelectedTipoProducto(newValue);
                      setTipoProductoInputValue(newValue?.name ?? "");
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setTipoProductoInputValue(newInputValue);
                      }
                    }}
                    isOptionEqualToValue={(option, val) =>
                      String(option.productTypeID) === String(val.productTypeID)
                    }
                    noOptionsText="No se encontraron tipos de producto"
                    loadingText="Buscando…"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de producto"
                        required
                        error={!!errors.tipoProductoId}
                        helperText={
                          loadingTipos
                            ? "Buscando tipos de producto…"
                            : errors.tipoProductoId?.message
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingTipos ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="productoId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    disabled={!tipoProductoId}
                    open={productoOpen}
                    onOpen={() => setProductoOpen(true)}
                    onClose={() => setProductoOpen(false)}
                    options={productoOptions}
                    getOptionLabel={(option) =>
                      `${option.code} — ${option.description}`
                    }
                    loading={loadingProductos}
                    inputValue={productoInputValue}
                    value={
                      productoOptions.find(
                        (p) => p.productID === field.value,
                      ) ||
                      selectedProducto ||
                      null
                    }
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.productID ?? "");
                      setSelectedProducto(newValue);
                      setProductoInputValue(
                        newValue
                          ? `${newValue.code} — ${newValue.description}`
                          : "",
                      );
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setProductoInputValue(newInputValue);
                      }
                    }}
                    isOptionEqualToValue={(option, val) =>
                      option.productID === val.productID
                    }
                    noOptionsText={
                      tipoProductoId
                        ? "No se encontraron productos"
                        : "Elegí primero un tipo de producto"
                    }
                    loadingText="Buscando…"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Producto (opcional)"
                        helperText={
                          loadingProductos ? "Buscando productos…" : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingProductos ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />

              <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>
                <Controller
                  name="porcentaje"
                  control={control}
                  rules={{
                    required: "El porcentaje es obligatorio",
                    validate: (v) => {
                      const n = Number(String(v).replace(",", "."));
                      if (Number.isNaN(n)) return "Ingresá un número válido";
                      if (n < 0 || n > 100)
                        return "El valor debe estar entre 0 y 100";
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          field.onChange("");
                          return;
                        }
                        const n = Number(String(raw).replace(",", "."));
                        if (Number.isNaN(n)) {
                          field.onChange(raw);
                          return;
                        }
                        if (n > 100) {
                          field.onChange("100");
                          return;
                        }
                        if (n < 0) {
                          field.onChange("0");
                          return;
                        }
                        field.onChange(raw);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      fullWidth
                      type="number"
                      label="Porcentaje de cupo"
                      required
                      inputProps={{ step: 1 }}
                      error={!!errors.porcentaje}
                      helperText={errors.porcentaje?.message ?? "Entre 0 y 100"}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ display: "flex", mr: 0.5 }}>
                            <PercentIcon color="action" fontSize="small" />
                          </Box>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ minWidth: 120 }}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: 140 }}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Guardar
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CuposProductoCreatePage;
